import JSZip from "jszip";

/**
 * Parses a KMZ or KML file and extracts points and teams.
 * @param {File} file - The file to parse.
 * @returns {Promise<{points: Array, teams: Array}>}
 */
export async function parseKMZ(file) {
    const isKMZ = file.name.toLowerCase().endsWith(".kmz");
    let kmlText;

    if (isKMZ) {
        const arrayBuffer = await file.arrayBuffer();
        const zip = await JSZip.loadAsync(arrayBuffer);
        const kmlName = Object.keys(zip.files).find((n) => n.toLowerCase().endsWith(".kml"));
        if (!kmlName) throw new Error("KMZ içinde KML bulunamadı.");
        kmlText = await zip.files[kmlName].async("text");
    } else {
        kmlText = await file.text();
    }

    const parser = new DOMParser();
    const kmlDoc = parser.parseFromString(kmlText, "text/xml");

    // 1) Extract Teams
    const teams = parseKMLTeams(kmlDoc);

    // 2) Extract Points
    const points = parseKMLPoints(kmlDoc);

    return { points, teams };
}

function parseKMLTeams(kmlDoc) {
    const importedTeams = [];
    const folders = Array.from(kmlDoc.getElementsByTagName("Folder"));
    let teamsFolder = folders.find(f => {
        const nameNode = f.getElementsByTagName("name")[0];
        return nameNode && nameNode.textContent === "__PAFTA_TEAMS__";
    });

    if (teamsFolder) {
        const placemarks = Array.from(teamsFolder.getElementsByTagName("Placemark"));
        placemarks.forEach((pm, idx) => {
            const extended = pm.getElementsByTagName("ExtendedData")[0];
            if (!extended) return;

            let localId = null;
            let teamName = "";
            let membersRaw = "";

            Array.from(extended.getElementsByTagName("Data")).forEach((d) => {
                const nameAttr = d.getAttribute("name");
                const valueNode = d.getElementsByTagName("value")[0];
                const raw = valueNode ? valueNode.textContent || "" : "";
                if (nameAttr === "teamLocalId") localId = raw;
                else if (nameAttr === "teamName") teamName = raw;
                else if (nameAttr === "members") membersRaw = raw;
            });

            const members = membersRaw ? membersRaw.split("||").map(m => m.trim()).filter(m => m) : [];
            importedTeams.push({
                localId: localId || teamName || `team-${idx + 1}`,
                name: teamName || `Ekip ${idx + 1}`,
                members,
            });
        });
    }
    return importedTeams;
}

function parseKMLPoints(kmlDoc) {
    const placemarks = Array.from(kmlDoc.getElementsByTagName("Placemark"));
    const points = [];
    const timestampBase = Date.now();

    placemarks.forEach((pm, idx) => {
        const coordNode = pm.getElementsByTagName("coordinates")[0];
        if (!coordNode) return;

        const nameNode = pm.getElementsByTagName("name")[0];
        const name = nameNode ? nameNode.textContent : `nokta-${idx + 1}`;
        const [lonStr, latStr] = coordNode.textContent.trim().split(/[,\s]+/);
        const lat = parseFloat(latStr);
        const lon = parseFloat(lonStr);

        const point = {
            id: null,
            name,
            lat,
            lng: lon,
            status: "pending",
            ekipId: null,
            customMeasurements: {},
            notMetni: "",
        };

        const extended = pm.getElementsByTagName("ExtendedData")[0];
        if (extended) {
            Array.from(extended.getElementsByTagName("Data")).forEach((d) => {
                const nameAttr = d.getAttribute("name");
                const valueNode = d.getElementsByTagName("value")[0];
                const raw = valueNode ? valueNode.textContent.trim() : "";
                const v = raw === "" ? null : raw;

                if (nameAttr === "id") point.id = v;
                else if (nameAttr === "status") point.status = v || "pending";
                else if (nameAttr === "ekipId") point.ekipId = v;
                else if (nameAttr === "il") point.il = v;
                else if (nameAttr === "ilce") point.ilce = v;
                else if (nameAttr === "koy") point.koy = v;
                else if (nameAttr === "adresTamamlandi") point.adresTamamlandi = v?.toLowerCase() === "true";
                else if (nameAttr === "sicaklik") point.sicaklik = v != null ? Number(v) : null;
                else if (nameAttr === "atm") point.atm = v != null ? Number(v) : null;
                else if (nameAttr === "spc") point.spc = v != null ? Number(v) : null;
                else if (nameAttr === "tds") point.tds = v != null ? Number(v) : null;
                else if (nameAttr === "sal") point.sal = v != null ? Number(v) : null;
                else if (nameAttr === "ph") point.ph = v != null ? Number(v) : null;
                else if (nameAttr === "veriGirisiTimestamp") point.veriGirisiTimestamp = v != null ? Number(v) : null;
                else if (nameAttr === "statusUpdatedAt") point.statusUpdatedAt = v != null ? Number(v) : null;
                else if (nameAttr === "notMetni") point.notMetni = v != null ? String(v) : "";
                else if (nameAttr === "customMeasurements") {
                    try {
                        const parsed = JSON.parse(String(v));
                        if (parsed && typeof parsed === "object") point.customMeasurements = parsed;
                    } catch (e) { }
                }
            });
        }

        if (!point.id) point.id = String(timestampBase + idx);
        points.push(point);
    });

    return points;
}
